"use client";

import { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'next/navigation';
import { useImoveis, type ImovelWithAgent } from '@/features/imoveis/useImoveis';
import { parseFilters, TIPO_DB_VALUES, type ImoveisFilters } from '@/lib/imoveis-filters';
import { buildListingH1 } from '@/lib/listing-metadata';
import ImovelCard from '@/features/imoveis/ImovelCard';
import ImovelCardSkeleton from '@/features/imoveis/ImovelCardSkeleton';
import dynamic from 'next/dynamic';
const ImovelFiltersFull = dynamic(
  () => import('@/features/imoveis/ImovelFiltersFull'),
  {
    loading: () => (
      <div className="space-y-3" aria-hidden="true">
        <div className="h-12 bg-slate-100 animate-pulse rounded-full" />
        <div className="h-10 bg-slate-100 animate-pulse rounded-full" />
      </div>
    ),
    ssr: false,
  }
);
import { useTenantContext } from '@/contexts/TenantContext';
import TenantHeader from '@/components/tenant/TenantHeader';
import TenantFooter from '@/components/tenant/TenantFooter';
import Link from 'next/link';

export type SerializedImovel = {
  id: string;
  title: string;
  price: number;
  property_code: string | null;
  property_type: string | null;
  transaction_type: string | null;
  images: string[] | null;
  status: string | null;
  bedrooms: number | null;
  bathrooms: number | null;
  parking_spaces: number | null;
  total_area: number | null;
  built_area: number | null;
  area_unit: string | null;
  built_area_unit: string | null;
  neighborhood: string | null;
  city: string | null;
  state: string | null;
  building_name: string | null;
  description: string | null;
};

interface BreadcrumbItem {
  name: string;
  href: string;
}

interface TenantListagemPageProps {
  subdomain: string;
  /** Texto base do H1 (sem count). Opcional: nas pages com `tipo` o componente
   *  recomputa reativamente via buildListingH1; a page só-finalidade passa fixo. */
  h1Base?: string;
  h1CountSSR: number;
  finalidadeDb?: string;
  finalidade?: string;
  tipoDb?: string;
  tipo?: string;
  bairroNome?: string;
  bairro?: string;
  initialImoveis: SerializedImovel[];
  initialTotal: number;
  breadcrumbs: BreadcrumbItem[];
  cidade?: string;
  imoveisCount?: number;
}

type FilterState = {
  search: string;
  propertyType: string;
  transactionType: string;
  priceRange: string;
  bedrooms: string;
  bathrooms: string;
  area: string;
  neighborhood: string;
  status: string;
  features: string[];
  sortBy: string;
  city: string;
  minPrice: string;
  maxPrice: string;
  parkingSpots: string;
  condominioMin: string;
  condominioMax: string;
  areaMin: string;
  areaMax: string;
};

export default function TenantListagemPage({
  subdomain,
  h1Base,
  h1CountSSR,
  finalidadeDb,
  finalidade,
  tipoDb,
  tipo,
  bairroNome,
  bairro,
  initialImoveis,
  initialTotal,
  breadcrumbs,
  cidade,
  imoveisCount,
}: TenantListagemPageProps) {
  const { tenant, isLoading, error } = useTenantContext();

  // 🎯 Filtros vindos da URL (landing pages de Google Ads). Precisam ser
  // replicados no fetch client-side para manter a paginação (scroll infinito)
  // consistente com o resultado filtrado do SSR.
  const searchParams = useSearchParams();
  const urlFilters = parseFilters(Object.fromEntries(searchParams.entries()));
  const subtypeDbValue = urlFilters.tipo ? TIPO_DB_VALUES[urlFilters.tipo] : undefined;

  const defaultFilters: FilterState = {
    search: bairroNome || '',
    propertyType: tipoDb || 'all',
    transactionType: finalidadeDb ?? 'all',
    priceRange: 'all',
    bedrooms: 'all',
    bathrooms: 'all',
    area: 'all',
    neighborhood: bairroNome || 'all',
    status: 'all',
    features: [],
    sortBy: 'newest',
    city: 'Todas as Cidades',
    minPrice: '',
    maxPrice: '',
    parkingSpots: 'all',
    condominioMin: '',
    condominioMax: '',
    areaMin: '',
    areaMax: '',
  };

  const [filters, setFilters] = useState<FilterState>(defaultFilters);

  const hasActiveFilters =
    (bairroNome ? filters.search !== bairroNome && filters.search !== '' : filters.search !== '') ||
    (!tipoDb && filters.propertyType !== 'all') ||
    filters.priceRange !== 'all' ||
    filters.bedrooms !== 'all' ||
    filters.bathrooms !== 'all' ||
    filters.minPrice !== '' ||
    filters.maxPrice !== '' ||
    filters.parkingSpots !== 'all' ||
    filters.condominioMin !== '' ||
    filters.condominioMax !== '' ||
    filters.areaMin !== '' ||
    filters.areaMax !== '' ||
    filters.features.length > 0;

  const imoveisFilters = {
    transactionType: finalidadeDb ?? 'all',
    // Subtipo da URL (?tipo=cobertura) tem prioridade sobre o tipo da rota.
    propertyType: subtypeDbValue ?? tipoDb ?? (filters.propertyType !== 'all' ? filters.propertyType : undefined),
    search: filters.search || undefined,
    priceRange: filters.priceRange !== 'all' ? filters.priceRange : undefined,
    minPrice: urlFilters.preco_min ? String(urlFilters.preco_min) : (filters.minPrice || undefined),
    maxPrice: urlFilters.preco_max ? String(urlFilters.preco_max) : (filters.maxPrice || undefined),
    features: filters.features.length > 0 ? filters.features : undefined,
    // 🎯 Quartos/Banheiros/Vagas usam semântica gte (padrão de mercado: "3+").
    // A seleção na UI tem prioridade; senão usa o valor da URL (landing).
    bedroomsMin: filters.bedrooms !== 'all' ? filters.bedrooms : (urlFilters.quartos ? String(urlFilters.quartos) : undefined),
    bathroomsMin: filters.bathrooms !== 'all' ? filters.bathrooms : undefined,
    parkingMin: filters.parkingSpots !== 'all' ? filters.parkingSpots : (urlFilters.vagas ? String(urlFilters.vagas) : undefined),
    suitesMin: urlFilters.suites ? String(urlFilters.suites) : undefined,
    // Área: seleção da UI tem prioridade; senão usa a URL (landing).
    areaMin: filters.areaMin || (urlFilters.area_min ? String(urlFilters.area_min) : undefined),
    areaMax: filters.areaMax || (urlFilters.area_max ? String(urlFilters.area_max) : undefined),
    // Condomínio: somente UI (não é param de landing).
    condominioMin: filters.condominioMin || undefined,
    condominioMax: filters.condominioMax || undefined,
    isLuxury: urlFilters.categoria === 'alto-padrao' ? true : undefined,
  };

  // 🎯 H1 reativo: começa com o valor SSR (URL) e recomputa quando o usuário
  // muda Quartos na UI. Só `quartos` da UI entra no H1 (Banheiros/Vagas não
  // aparecem no texto, padrão Zap). Demais campos (suítes/categoria/status/
  // referência/tipo) vêm da URL.
  const activeFilters: ImoveisFilters = {
    ...urlFilters,
    quartos:
      filters.bedrooms !== 'all' ? Number(filters.bedrooms) : urlFilters.quartos,
  };
  // Se a page passou h1Base fixo (page só-finalidade), usa-o. Caso contrário
  // (pages com tipo, ou page raiz /imoveis sem segmentos), recomputa reativo.
  const reactiveH1Base =
    h1Base !== undefined
      ? h1Base
      : buildListingH1({
          finalidade,
          tipoRota: tipo,
          bairro: bairroNome ?? null,
          cidade,
          filters: activeFilters,
        });

  const {
    imoveis,
    loading: imoveisLoading,
    hasMore,
    fetchMore,
    total,
    erro: erroDaBusca,
  } = useImoveis(8, imoveisFilters, 4, initialImoveis as unknown as ImovelWithAgent[], initialTotal);

  // Usa dados server-side enquanto o hook ainda não carregou
  const displayImoveis: SerializedImovel[] =
    imoveisLoading && imoveis.length === 0 ? initialImoveis : (imoveis as unknown as SerializedImovel[]);
  const displayTotal = imoveisLoading && imoveis.length === 0 ? h1CountSSR : total;

  const observerTarget = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !imoveisLoading) {
          fetchMore();
        }
      },
      { threshold: 0.1 }
    );
    const target = observerTarget.current;
    if (target) observer.observe(target);
    return () => { if (target) observer.unobserve(target); };
  }, [hasMore, imoveisLoading, fetchMore]);

  const onClearFilters = () => setFilters(defaultFilters);

  if (isLoading || !tenant) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <p className="text-red-600">Erro ao carregar. Tente novamente.</p>
      </div>
    );
  }

  const config = tenant.configurations || {};
  const pageStyle = {
    backgroundColor: (config as any).cor_fundo || '#f8fafc',
    color: (config as any).cor_texto || '#1f2937',
  };

  return (
    <div className="tenant-theme" style={pageStyle}>
      <TenantHeader subdomain={subdomain} imoveisCount={imoveisCount} />

      <main className="min-h-screen bg-gray-50 py-8">
        <div className="container mx-auto px-4">

          {/* Breadcrumb */}
          <nav className="mb-4 text-sm text-slate-500" aria-label="Breadcrumb">
            <ol className="flex flex-wrap items-center gap-1.5">
              {breadcrumbs.map((crumb, idx) => (
                <li key={crumb.href} className="flex items-center gap-1.5">
                  {idx < breadcrumbs.length - 1 ? (
                    <>
                      <Link href={crumb.href} className="hover:text-slate-700 underline">
                        {crumb.name}
                      </Link>
                      <span className="text-slate-400" aria-hidden="true">&rsaquo;</span>
                    </>
                  ) : (
                    <span className="text-slate-700 font-medium">{crumb.name}</span>
                  )}
                </li>
              ))}
            </ol>
          </nav>

          {/* H1 padrão Zap: count reativo (useImoveis.total) + texto base.
              No primeiro paint usa o count SSR; atualiza ao vivo ao mudar filtros. */}
          <div className="mb-6">
            <h1 className="text-2xl md:text-3xl font-bold text-slate-800">
              {displayTotal.toLocaleString('pt-BR')} {reactiveH1Base}
            </h1>
          </div>

          {/* Filtros */}
          <div className="mb-8">
            <ImovelFiltersFull
              filters={filters}
              setFilters={setFilters}
              hasActiveFilters={hasActiveFilters}
              onClearFilters={onClearFilters}
            />
          </div>

          {/* Grade de imóveis */}
          <div>
            {imoveisLoading && imoveis.length === 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                {Array.from({ length: 8 }).map((_, i) => (
                  <ImovelCardSkeleton key={`sk-${i}`} />
                ))}
              </div>
            ) : displayImoveis.length === 0 ? (
              <div className="text-center py-12">
                {/* Lista vazia por LIMITE não é o mesmo que lista vazia por
                    não haver resultado: o visitante que ajustasse os filtros
                    continuaria sem nada, porque o problema é outro e passa
                    sozinho. A rota responde 429 com o motivo justamente para
                    poder ser dito aqui. */}
                <div className="text-gray-400 text-6xl mb-4">{erroDaBusca ? '⏳' : '🏠'}</div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">
                  {erroDaBusca
                    ? 'Muitas buscas em pouco tempo'
                    : hasActiveFilters
                      ? 'Nenhum imóvel encontrado'
                      : 'Nenhum imóvel disponível'}
                </h3>
                <p className="text-gray-600 mb-4">
                  {erroDaBusca
                    ? erroDaBusca
                    : hasActiveFilters
                      ? 'Tente ajustar os filtros para encontrar mais imóveis.'
                      : 'Nossos imóveis estarão disponíveis em breve.'}
                </p>
                {hasActiveFilters && !erroDaBusca && (
                  <button
                    onClick={onClearFilters}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    Limpar filtros
                  </button>
                )}
              </div>
            ) : (
              <>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 mb-8">
                  {displayImoveis.map((imovel, idx) => (
                    <ImovelCard key={imovel.id} imovel={imovel} priority={idx < 3} />
                  ))}
                </div>

                {imoveisLoading && imoveis.length > 0 && (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 mb-8">
                    {Array.from({ length: 4 }).map((_, i) => (
                      <ImovelCardSkeleton key={`sk-more-${i}`} />
                    ))}
                  </div>
                )}

                {hasMore && (
                  <div ref={observerTarget} className="h-10 flex items-center justify-center">
                    <p className="text-gray-500 text-sm">Carregando mais imóveis...</p>
                  </div>
                )}

                {!hasMore && imoveis.length > 0 && (
                  <div className="text-center py-8">
                    <p className="text-gray-500 text-sm">
                      Você visualizou todos os {displayTotal} imóveis disponíveis
                    </p>
                  </div>
                )}
              </>
            )}
          </div>

        </div>
      </main>

      <TenantFooter subdomain={subdomain} />
    </div>
  );
}
